import { useState } from 'react';
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Modal,
  ModalBody,
} from 'reactstrap';
import { AdminPreQualificationFormQuestion } from '../models/preQualificationModel';
import {
  formatAmount,
  getSourceFileUrl,
  humanize,
  isIncomeSourcesValue,
} from '../helpers/incomeSourceHelper';
import {
  IconBriefcase,
  IconBuilding,
  IconBuildingBank,
  IconMoneybag,
  IconSignature,
  IconUser,
} from '@tabler/icons-react';

interface Props {
  questions: AdminPreQualificationFormQuestion[];
}

const categoryIcons = {
  projectList: IconBuilding,
  loanDetails: IconMoneybag,
  borrowerInfo: IconUser,
  borrowerJob: IconBriefcase,
  terms: IconSignature,
  default: IconBuildingBank,
};

const categoryColors = {
  projectList: 'info',
  loanDetails: 'success',
  borrowerInfo: 'primary',
  borrowerJob: 'default',
  terms: 'warning',
  default: 'warning',
};

// Add these helper functions at the top of the file
const isImageUrl = (url: string): boolean => {
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
};

const isFileUrl = (url: string): boolean => {
  return /\.(pdf|doc|docx|xls|xlsx|txt)$/i.test(url);
};

const getFileIcon = (url: string): string => {
  if (/\.pdf$/i.test(url)) return 'fas fa-file-pdf';
  if (/\.(doc|docx)$/i.test(url)) return 'fas fa-file-word';
  if (/\.(xls|xlsx)$/i.test(url)) return 'fas fa-file-excel';
  return 'fas fa-file';
};

const getFileExtension = (url: string): string => {
  return url.split('.').pop()?.toUpperCase() || '';
};

const PreQualificationDetailFormPQResponse = ({ questions }: Props) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const getIcon = (categoryId: string) => {
    return (
      categoryIcons[categoryId as keyof typeof categoryIcons] ||
      categoryIcons.default
    );
  };

  const getColor = (categoryId: string) => {
    return (
      categoryColors[categoryId as keyof typeof categoryColors] ||
      categoryColors.default
    );
  };

  const handleImageClick = (imgUrl: string) => {
    setSelectedImage(imgUrl);
    setIsModalOpen(true);
  };

  const handleFileClick = (fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  return (
    <>
      {questions.map((q) => {
        const Icon = getIcon(q.id);
        const badgeColor = getColor(q.id);

        return (
          <Card key={q.id} className="mb-4 shadow">
            <CardHeader className="bg-light">
              <div className="d-flex align-items-center">
                <Icon size={20} className="text-primary me-2" />
                <CardTitle tag="h3" className="mb-0 text-capitalize me-3">
                  {q.title}
                </CardTitle>
                <div className="ms-auto">
                  <Badge color={badgeColor}>
                    {q.responses.length}{' '}
                    {q.responses.length === 1 ? 'Respuesta' : 'Respuestas'}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {q.responses.map((answer, index) => (
                <div key={index} className="ps-3 py-2 mb-3">
                  <small className="text-muted fw-medium">{answer.label}</small>
                  <div className="fw-bold">
                    {isIncomeSourcesValue(answer.value) ? (
                      <div className="d-flex flex-wrap mt-2">
                        {answer.value.sources.map((source) => (
                          <div
                            key={source.id}
                            className="rounded border p-3 mr-3 mb-3"
                            style={{
                              minWidth: '220px',
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                            }}
                            title="Abrir documento"
                            onClick={() =>
                              handleFileClick(getSourceFileUrl(source))
                            }
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'scale(1.03)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <div className="d-flex align-items-center mb-2">
                              <i
                                className={`${getFileIcon(
                                  source.filePath
                                )} fa-2x mr-2 text-primary`}
                              />
                              <div>
                                <div className="small">{source.fileName}</div>
                                <div className="small text-muted">
                                  {getFileExtension(source.filePath)}
                                </div>
                              </div>
                            </div>
                            <div className="mb-1">
                              <Badge color="info" className="mr-1">
                                {humanize(source.type)}
                              </Badge>
                              {source.detectedType && (
                                <Badge color="secondary" className="mr-1">
                                  {source.detectedType.toUpperCase()}
                                </Badge>
                              )}
                              {source.origin === 'ai' && (
                                <Badge color="primary">IA</Badge>
                              )}
                            </div>
                            <div className="small">
                              Ingreso mensual:{' '}
                              {formatAmount(
                                source.monthlyAmount,
                                source.currency
                              )}
                            </div>
                            {Object.entries(source.fields || {}).map(
                              ([key, fieldValue]) => (
                                <div
                                  key={key}
                                  className="small text-muted font-weight-normal"
                                >
                                  {humanize(key)}: {String(fieldValue)}
                                </div>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    ) : Array.isArray(answer.value) ? (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {answer.value.map((file, fileIndex) => (
                          <div key={fileIndex}>
                            {isImageUrl(file) ? (
                              // Image preview
                              <img
                                src={file}
                                alt={`${answer.label} ${fileIndex + 1}`}
                                className="rounded cursor-pointer"
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s',
                                }}
                                onClick={() => handleImageClick(file)}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform =
                                    'scale(1.05)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              />
                            ) : isFileUrl(file) ? (
                              // File preview
                              <div
                                className="rounded border p-2 text-center cursor-pointer"
                                style={{
                                  width: '100px',
                                  transition: 'transform 0.2s',
                                }}
                                onClick={() => handleFileClick(file)}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform =
                                    'scale(1.05)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i
                                  className={`${getFileIcon(file)} fa-2x mb-2`}
                                />
                                <div className="small text-muted">
                                  {getFileExtension(file)}
                                </div>
                              </div>
                            ) : (
                              // Regular link
                              <a
                                href={file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary"
                              >
                                <i className="fas fa-external-link-alt me-1" />
                                Ver enlace
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : typeof answer.value === 'boolean' ? (
                      // Handle boolean values
                      <Badge color={answer.value ? 'success' : 'danger'}>
                        {answer.value ? 'Sí' : 'No'}
                      </Badge>
                    ) : typeof answer.value === 'string' ||
                      typeof answer.value === 'number' ? (
                      // Handle regular text values
                      answer.value
                    ) : null}
                  </div>
                </div>
              ))}
            </CardBody>
          </Card>
        );
      })}

      {/* Image Preview Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
        <ModalBody className="p-0">
          {selectedImage && (
            <div className="position-relative">
              <img
                src={selectedImage}
                alt="Preview"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
              <button
                className="btn btn-icon btn-sm btn-light position-absolute top-0 end-0 m-2"
                onClick={toggleModal}
                style={{
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                }}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default PreQualificationDetailFormPQResponse;
